import React, { useEffect, useRef, useState } from 'react';
import ls from 'local-storage';
import { errorText, fetchPopups, sendPopupContact, sendPopupEvent } from './api';
import PopupModal from './PopupModal';
import { commeSiteCommande } from './modeles';

// Shows, one after the other, the pop-ups the API returns for this page (in the order of
// /admin-popups) and sends what the user did with each (the API decides when it comes back: routes/popups).
//   placement : 'menu' or the route (/tableau-de-bord…), matched with Popup.placement
//   actions   : { [cta_action]: (popup) => … } of the page, they replace the default ones below;
//               without an action, the main button opens cta_url (back-office route or https://…)
//   onSettled : called once per page when the list is over: true if a pop-up was shown, false if none
// Every button goes on to the next pop-up of the list, the main one included. When it opens another
// page, the pop-ups not shown yet come first on that page (pending), so none is forgotten.
// cta_action « contact »: one click, the API emails the team with the account infos; the modal stays
// open and shows « Envoi en cours… », then the confirmation (« Continuer » goes on) or the error (retry).

// Actions available on every page
const DEFAULT_ACTIONS = {
  otp_activate: (popup, history) => history && history.push('/mon-compte?2fa=1'),
};

// Clicked in the last seconds: not shown again while the click is on its way to the API (the next
// page may ask for its list before). Everything else is decided by the API: « Réafficher » in
// /admin-popups shows them again without reloading the page.
const recent = new Map(); // id -> time of the click
const RECENT_MS = 8 * 1000;
const clickedRecently = (id) => Date.now() - (recent.get(id) || 0) < RECENT_MS;
// Not shown yet when the user left the page: shown first on the next one
let pending = [];

export default function PopupHost({ placement, actions = {}, history, onSettled, zIndex }) {
  const [queue, setQueue] = useState([]);
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState(null); // { etat: 'envoi' | 'envoye' | 'erreur', erreur }
  const settled = useRef(onSettled);
  settled.current = onSettled;
  const state = useRef({ placement: null });
  const queueRef = useRef([]);
  queueRef.current = queue;
  const displayed = useRef(new Set()); // shown by this page

  const settle = (wasShown) => settled.current && settled.current(wasShown);
  const popup = queue[0] || null;

  useEffect(() => {
    state.current = { placement };
    displayed.current = new Set();
    setQueue([]);
    setOpen(false);
    const carried = pending.filter((p) => !clickedRecently(p.id));
    pending = [];
    if (!placement || !ls.get('token')) {
      settle(false);
      return undefined;
    }

    // one more try a little later: the database sometimes drops the first connection
    const load = () => fetchPopups(placement).catch(() => new Promise((r) => setTimeout(r, 2500)).then(() => fetchPopups(placement)));
    load()
      .catch(() => [])
      .then((list) => {
        if (state.current.placement !== placement) return;
        const ids = new Set(carried.map((p) => p.id));
        const todo = [...carried, ...list.filter((p) => !ids.has(p.id) && !clickedRecently(p.id))].map(commeSiteCommande);
        if (!todo.length) {
          settle(false);
          return;
        }
        setQueue(todo);
      });

    // leaving the page (main button to another page, menu → point de vente…): the rest goes to the next one
    return () => {
      pending = queueRef.current.filter((p) => !displayed.current.has(p.id));
      state.current = { placement: null };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement]);

  // Each pop-up of the list, when it comes first
  useEffect(() => {
    if (!popup) return;
    setContact(null);
    displayed.current.add(popup.id);
    setOpen(true);
    sendPopupEvent(popup.id, 'seen');
  }, [popup]);

  // next one once the closing animation is over
  const next = (delay = 300) => {
    const forPlacement = state.current.placement;
    setTimeout(() => {
      if (state.current.placement !== forPlacement) return;
      const rest = queueRef.current.slice(1);
      setQueue(rest);
      if (!rest.length) settle(true);
    }, delay);
  };

  const onAction = (action) => {
    const p = popup;
    if (!p) return;
    const etat = contact && contact.etat;
    if (etat === 'envoi') return;

    // « Je souhaite être contacté »: the contact route emails the team and records the click itself
    recent.set(p.id, Date.now());
    if (action === 'done' && p.cta_action === 'contact') {
      setContact({ etat: 'envoi' });
      sendPopupContact(p.id)
        .then(() => setContact({ etat: 'envoye' }))
        .catch((e) => setContact({ etat: 'erreur', erreur: errorText(e, "L'envoi de votre demande a échoué. Réessayez dans un instant.") }));
      return;
    }

    setOpen(false);
    // after the confirmation « Continuer » / ×: the request is already recorded as done
    if (etat === 'envoye') {
      next();
      return;
    }
    sendPopupEvent(p.id, action);

    if (action === 'done') {
      const run = actions[p.cta_action] || DEFAULT_ACTIONS[p.cta_action];
      if (run) run(p, history);
      else if (p.cta_url && p.cta_url.startsWith('/') && history) history.push(p.cta_url);
      else if (p.cta_url) window.open(p.cta_url, '_blank', 'noopener,noreferrer');
    }
    next();
  };

  return <PopupModal popup={popup} open={open} onAction={onAction} zIndex={zIndex} contact={contact} />;
}
